import * as guid from "guid";
import { uniqueKeySeparator } from "./IWebClientMetricsLogger";
import { MetricsContext, createContext } from "./MetricsContext";
import { MetricsSuites } from "./MetricsSuites";

// All the values in this enum must be unique. Keep the string length short while maintain the uniqueness.
export enum EptBreakdownMetricNames {
  // Elapsed time is just a time span from one point to another. These two points might not be directly related
  // Time spent on refers to elapsed time of an action. The start and end points are directly related.
  basicPageLoadMetrics = "bt",
  advancedPageLoadMetrics = "at",
  incarnation = "i",
  timeOrigin = "to",
  // Total number of worksheets
  numberOfWorksheets = "ws",
  // Total number of tabs
  numberOfTabs = "tb",
  // This is only used to mark current viz's embed mode
  embedMode = "em",
  // This is used to mark the apiID of current viz load
  apiId = "aid",
  // This marks the rendering mode
  renderMode = "rm",
  // This is only used to mark the navigation type of the current page load
  navigationType = "nt",
  // This is only used in font load to mark a font file as already loaded
  alreadyLoaded = "al",
  // Measured from timeOrigin to responseEnd (static html starts to load)
  responseEnd = "re",
  // Time spent to load static html file
  loadStaticHtml = "lsh",
  // This is TTFB
  responseStart = "ep",
  // Load viewerbootstrap module
  loadViewerBootstrapModule = "lvb",
  // Load all vql modules
  loadVqlModules = "lvql",
  // Measure from ttfb to ViewerBootstrap mdoule loaded
  appStartEpoch = "aep",
  // Measure the startSession request from vizclient side - from issuing the request to receiving the response
  startSession = "ss",
  // Measure from TTFB to bootstrapSession request sent
  initBootstrapRequest = "ibr",
  // Time spend on handling TsConfig in prebootstrap
  handleTsConfig = "htc",
  // Time spend to prepare startSession request before fetch()
  prepareStartSession = "pss",
  // Time spent to load static css
  loadCss = "lcss",
  // Time spent on bootstrapSession when onInitial callback is triggered. This instrumentation is on TS layer
  // which includes extras that are not measured in BTSTRP. These two measurements can work together to identify
  // regressions in the delta
  bootstrapOnInitial = "boi",
  // Time spent on bootstrapSession when onError callback is triggered.
  bootstrapOnErr = "boe",
  // Time spent on bootstrapSession when onSecondaryAction callback is triggered. This instrumentation is on TS layer
  // which includes extras that are not measured in BTSTRP. These two measurements can work together to identify
  // regressions in the delta
  bootstrapOnSecondary = "bos",
  // Elapsed time of a notified bootstrap. This means the page is embedded and waits for host page to tell us to bootstrap
  notifiedBootstrap = "ntb",
  // Elapsed time of a notified bootstrap that returns error.
  notifiedBootstrapWithError = "nte",
  // Mark a bootstrap as a bootstrap w/o route
  bootstrapWithoutRoute = "bwr",
  // Elapsed time from timeOrigin when bootstrap POST request is actually sent
  bootstrapPostRequestSent = "bps",
  // Elapsed time for scale image function to get called during bootstrapOnMouseOver
  bootstrapFitImageOnMouseOver = "bfm",
  // Elapsed time to fallback to immediate bootstrap when static image is not retrievable during bootstrapOnMouseOver
  bootstrapResetOnMouseOver = "brm",
  // Time spent on process the primary payload of bootstrap.
  processPrimaryPayload = "propri",
  // Time spent on initializing presmodel.
  initializeModels = "mdlini",
  // Time spent on handling model events.
  handleModelEvents = "mdlevt",
  // Time spent on process the secondary payload of bootstrap.
  processSecondaryPayload = "prosec",
  // Time spent on progressively loading zones
  progressiveLoad = "pld",
  // Update any viz zones that are deferred with VizData coming from bootstrap's secondary payload.
  updateZonesWithVizData = "uz",
  // Update pres model by removing zones that are pending progressive loading.
  processModelUpdate = "pmu",
  // Elapsed time from bootstrapSession's onInitial callback to 'Client fully loaded'.
  clientFullyLoaded = "clntld",
  // Elapsed time from bootstrapSession's onInitial callback to all image tiles fully loaded
  imageTilesLoaded = "imgld",
  // Elapsed time from 'image tiles loaded' to all registered modules loaded
  registeredModulesLoaded = "rmld",
  // Elapsed time to load a module asyncronously
  moduleLoadAsync = "mdload",
  // Elapsed time to execute a remote command
  executeRemoteCommand = "exerem",
  // Elapsed time on a failed remote command execute
  executeRemoteCommandReturnError = "exeerr",
  // Elapsed time to process remote command's resonse
  processRemoteCommandResponse = "prorem",
  // Elapsed time to execute local command
  executeLocalCommand = "exeloc",
  // Elapsed time to process local command response
  processLocalCommandResponse = "proloc",
  // Time spent on post load operations. This is after initial layout complete, let backend to do cleanup
  // and retrive info not necessary to load the viz
  postLoadOperation = "plo",
  // Elapsed time of calling getSessionInfo
  getSessionInfo = "gsi",
  // Elapsed time of calling getSessionInfo that returns error
  getSessionInfoReturnError = "gse",
  // Elapsed time of calling GetAccelerationStateForView
  getAccelerationStateForView = "gas",
  // Elapsed time to request runtime data
  runtimeLoad = "rtload",
  // Elapsed time to download a font file
  downloadFont = "df",
  // Elapsed time to get toolbar layout
  toolbarLayout = "tbrlay",
  // Elapsed time to handle new toolbar
  toolbarHandleNewToolbar = "tbrhnt",
  // Elapsed time to add toolbar
  toolbarAddToolbar = "tbradd",
  // Elapsed time handle resize
  toolbarHandleResize = "tbrhre",
  // Elapsed time of runtime model presmodel conversion
  runtimeModelPresmodelConversion = "rtconv",
  // Elasped time since timeOrigin for client side application to start up
  applicationStartup = "appstr",
  // Elapsed time since timeOrigin to client becomes interactive
  applicationInteractive = "appint",
  // Elapsed time since timeOrigin to first paint in authoring mode
  firstPaintInAuthoring = "apppnt",
  // Page visibility change
  pageVisibilityChange = "pvc",
  // Total number of page being put to hidden (background tab or minimized)
  pageHiddenCount = "phc",
  // Total duration of page hidden time
  pageHiddenTime = "pht",
  // Page start loading as visible
  pageStartAsVisible = "psv",
  // Page end loading as visible
  pageEndAsVisible = "pev",
  // The offset from timeOrigin to the first hidden event
  firstHiddenOffset = "fho",
  // Any feature flags we want to track with this EPT
  featureFlags = "ffs",
}

// Global boolean to tell the phase of telemetry event. It has only two phases right now
// -    When it's true: EPT phase (page load phase). This is the initial value because when prebootstrap module is loaded,
//      it's guaranteed in EPT phase
// -    When it's false: None EPT phase (after page load complete and EPT metric logged)
let isDuringEpt = true;
export function setDuringEptFlag(flag: boolean): void {
  isDuringEpt = flag;
}
export function getDuringEptFlag(): boolean {
  return isDuringEpt;
}
// Global boolean to tell the page's visibility when start loading
// -    true: Page is visible when page load starts
// -    false: Page is hidden (background tab or minimized) whe page load starts
let isPageVisibleWhenStartLoading = true;
function setIsPageVisibleWhenStartLoading(flag: boolean): void {
  isPageVisibleWhenStartLoading = flag;
}
export function getIsPageVisibleWhenStartLoading(): boolean {
  return isPageVisibleWhenStartLoading;
}
// Global variable to store EPT breakdown MetricsContext. This includes the metric events that are fully completed,
// and those started but yet to complete
const eptBreakdownMetricsContexts = new Map<string, MetricsContext>();
// Global variable to store page visibility MetricsContext.
const pageVisibilityMetricsContexts = new Map<string, MetricsContext>();
// Function to create multiple ept breakdown metric context. When using this function, either not specifying extraInfo,
// or all metrics context share the same extraInfo
export function openEptMetricsContext(
  description: string[],
  extraInfo?: string
): void;
// Function to create a single ept breakdown metric context. When need to specify extraInfo, use this function
export function openEptMetricsContext(
  description: string,
  extraInfo?: string
): void;
// The implementation of openEptMetricsContext
export function openEptMetricsContext(
  description: string | string[],
  extraInfo?: string
): void {
  openBreakdownMetricsContext(
    eptBreakdownMetricsContexts,
    description,
    extraInfo
  );
}
function openPageVisibilityMetricsContext(description: string): void {
  openBreakdownMetricsContext(pageVisibilityMetricsContexts, description);
}
function openBreakdownMetricsContext(
  breakdownMetricsContexts: Map<string, MetricsContext>,
  description: string | string[],
  extraInfo?: string
): void {
  if (!isDuringEpt) {
    return;
  }
  if (typeof description == "string") {
    openMetricsContext(
      breakdownMetricsContexts,
      MetricsSuites.Bootstrap,
      description,
      extraInfo
    );
  } else if (Array.isArray(description)) {
    description.forEach((desp: string) => {
      openMetricsContext(
        breakdownMetricsContexts,
        MetricsSuites.Bootstrap,
        desp,
        extraInfo
      );
    });
  } else {
    console.error("Invalid parameter type passed to openEptMetricsContext");
  }
}

// Generate a unique key value based on the given sourceKey.
export function getUniqueKey(sourceKey: string): string {
  return sourceKey + uniqueKeySeparator + guid.raw();
}

// Generate a GUID string. This function is mainly used by C# side vizclient code.
export function rawGuid(): string {
  return guid.raw();
}

// Generate a EPT breakdown data point, which just marks the happening of an event, not the elapsed time of
// that event. This is uesful for things happened during EPT that we don't know or we dont' need to know
// its elapsed time however want to keep a record of it
export function markEptMetricsEvent(
  description: string,
  extraInfo?: string
): void {
  if (!isDuringEpt) {
    return;
  }
  openMetricsContext(
    eptBreakdownMetricsContexts,
    MetricsSuites.Bootstrap,
    description,
    extraInfo
  );
  closeEptMetricsContext(description);
}
function openMetricsContext(
  breakdownMetricsContexts: Map<string, MetricsContext>,
  metricsSuite: MetricsSuites,
  description: string,
  extraInfo?: string
): void {
  breakdownMetricsContexts.set(
    description,
    createContext(metricsSuite, description, extraInfo, true)
  );
}
export function closeEptMetricsContext(key: string): void {
  closeBreakdownMetricsContext(eptBreakdownMetricsContexts, key);
}
function closePageVisibilityMetricsContext(key: string): void {
  closeBreakdownMetricsContext(pageVisibilityMetricsContexts, key);
}
function closeBreakdownMetricsContext(
  breakdownMetricsContext: Map<string, MetricsContext>,
  key: string
) {
  if (!isDuringEpt) {
    return;
  }
  const metric = breakdownMetricsContext.get(key);
  if (metric) {
    metric.dispose();
  }
}
export function getOffsetFromTimeZero(
  description: string,
  extraInfo?: string
): void {
  if (!isDuringEpt) {
    return;
  }
  eptBreakdownMetricsContexts.set(
    description,
    createContext(MetricsSuites.Bootstrap, description, extraInfo, true, true)
  );
  closeEptMetricsContext(description);
}
// This function is used in MetricsController to get all metric contexts collected
// during over the course of EPT.
export function getEptBreakdownMetricsContexts(): Map<string, MetricsContext> {
  return eptBreakdownMetricsContexts;
}
// This function is used in MetricsController to get all page visibility metric context collected during page load
export function getPageVisibilityMetricsContexts(): Map<
  string,
  MetricsContext
> {
  return pageVisibilityMetricsContexts;
}
// This function is used in MetricsController to get the count of the page being put to hidden state
export function getPageToHiddenCount(): number {
  return setPageToHiddenCount;
}
// Variable to count total number of the page being put to hidden during one page load
let setPageToHiddenCount = 0;
// Set max count of page being put to hidden. Doing this to prevent abusive page visibility toggle from generating
// too much metrics data. We still count the total number of page being put to hidden though.
// Please see GUS: W-18048484 for the testing data and how 125 was picked as the new threshold value
const maxSetPageToHiddenCount = 125;
export function onVisibilityChange() {
  // If the page starts as hidden, count it in and set visibility boolean to false
  if (document.hidden) {
    setPageToHiddenCount++;
    openPageVisibilityMetricsContext(
      EptBreakdownMetricNames.pageVisibilityChange.toString() +
        setPageToHiddenCount.toString()
    );
    setIsPageVisibleWhenStartLoading(false);
  }

  // The handler that handles visibility change during page load. After the page load, the event listener of
  // this handler is removed
  const visbilityChangeHandler = () => {
    if (getDuringEptFlag()) {
      if (document.hidden) {
        setPageToHiddenCount++;
        // Create the page visibility metrics context only if the total page hidden count is less than the max
        if (setPageToHiddenCount <= maxSetPageToHiddenCount) {
          openPageVisibilityMetricsContext(
            EptBreakdownMetricNames.pageVisibilityChange.toString() +
              setPageToHiddenCount.toString()
          );
        }
        // If this is page becoming hidden for the first time, log the first hidden offset.
        // Be aware that the offset here is from time zero, not timeOrigin. So later in MetricsController, we need
        // to add Epoch to get the offset from timeOrigin
        if (setPageToHiddenCount === 1) {
          getOffsetFromTimeZero(
            EptBreakdownMetricNames.firstHiddenOffset.toString()
          );
        }
      } else {
        // Close the page visibility metrics context only if the total page hidden count is less than the max
        if (setPageToHiddenCount <= maxSetPageToHiddenCount) {
          closePageVisibilityMetricsContext(
            EptBreakdownMetricNames.pageVisibilityChange.toString() +
              setPageToHiddenCount.toString()
          );
        }
      }
    } else {
      // Remove this event listener after page load
      document.removeEventListener("visibilitychange", visbilityChangeHandler);
    }
  };

  document.addEventListener("visibilitychange", visbilityChangeHandler);
}
